import { useRef, useState } from 'react';
import { api } from '../../services/api';
import {
  BANNER_ACCEPTED_TYPES,
  checkBannerFile,
  cropToBanner,
  meetsMinimumSize,
  readImageDimensions,
} from './offerBannerImage';

export interface BannerValue {
  storageKey: string | null;
  url: string | null;
}

interface AdminBannerUploaderProps {
  offerId: string;
  token: string;
  label: string;
  hint?: string;
  value: BannerValue;
  onChange: (value: BannerValue) => void;
  /** Allow removing the banner (the Arabic banner is optional; the English one is required). */
  removable?: boolean;
  disabled?: boolean;
  error?: string | null;
  testId?: string;
}

type Phase = 'idle' | 'processing' | 'uploading';

/**
 * #997 — banner upload for an admin offer edit: JPG/PNG/WebP ≤ 5 MB, at least 1200×600, centre-cropped
 * to 2:1 (JPEG 0.85), then `POST …/banner/upload-url` → browser PUT to S3. The new storage key goes into
 * the PUT body; there is no confirm call. A failed upload keeps the current banner.
 * Mirrors the partner `BannerUploader` without the suggested-banner gallery.
 */
export default function AdminBannerUploader({
  offerId, token, label, hint, value, onChange, removable, disabled, error, testId,
}: AdminBannerUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [localError, setLocalError] = useState<string | null>(null);
  const busy = phase !== 'idle';

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled || busy) return;
    setLocalError(null);
    const typeOrSize = checkBannerFile(file);
    if (typeOrSize) {
      setLocalError(typeOrSize);
      return;
    }
    setPhase('processing');
    let cropped: File;
    try {
      const { width, height } = await readImageDimensions(file);
      if (!meetsMinimumSize(width, height)) {
        setLocalError('The image must be at least 1200 × 600 pixels.');
        setPhase('idle');
        return;
      }
      cropped = await cropToBanner(file);
    } catch {
      setLocalError('Could not read this image. Try another file.');
      setPhase('idle');
      return;
    }
    setPhase('uploading');
    try {
      const contentType = cropped.type || 'image/jpeg';
      const { uploadUrl, storageKey } = await api.getOfferBannerUploadUrl(
        offerId, { fileName: cropped.name, contentType, fileSize: cropped.size }, token);
      await api.putOfferBannerToStorage(uploadUrl, cropped, contentType);
      let preview: string | null = null;
      try { preview = URL.createObjectURL(cropped); } catch { preview = null; }
      onChange({ storageKey, url: preview });
    } catch {
      setLocalError('Upload failed. The current banner is kept.');
    } finally {
      setPhase('idle');
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const shownError = localError ?? error ?? null;
  const inputId = `${testId ?? 'offer-banner'}-file`;

  return (
    <div className="offer-edit__banner" data-testid={testId}>
      <label className="admin-form-label" htmlFor={inputId}>{label}</label>
      {hint && <p className="offer-edit__hint">{hint}</p>}
      {value.storageKey ? (
        value.url
          ? <img src={value.url} alt={`${label} preview`} className="offer-edit__banner-img" />
          : <div className="offer-edit__banner-img offer-edit__banner-img--empty">Banner set</div>
      ) : (
        <div className="offer-edit__banner-img offer-edit__banner-img--empty">No banner</div>
      )}
      <div className="offer-edit__banner-actions">
        <button
          type="button"
          className="admin-btn admin-btn-outline admin-btn-sm"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
        >
          {value.storageKey ? 'Replace' : 'Upload'}
        </button>
        {removable && value.storageKey && (
          <button
            type="button"
            className="admin-btn admin-btn-outline admin-btn-sm"
            disabled={disabled || busy}
            onClick={() => { setLocalError(null); onChange({ storageKey: null, url: null }); }}
          >
            Remove
          </button>
        )}
        {phase === 'processing' && <span className="admin-text-muted" role="status">Preparing image…</span>}
        {phase === 'uploading' && <span className="admin-text-muted" role="status">Uploading…</span>}
      </div>
      <p className="offer-edit__hint">JPG, PNG or WebP, up to 5 MB, at least 1200 × 600. Cropped to 2:1 from the centre.</p>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={BANNER_ACCEPTED_TYPES.join(',')}
        className="offer-edit__file"
        disabled={disabled || busy}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {shownError && <div className="offer-edit__error" role="alert">{shownError}</div>}
    </div>
  );
}
